import React, {useState} from "react";

import {Box} from '@mui/material';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {PageHeader} from "./PageHeader";
import {accessCtrl} from "../ctx/AccessCtrl";
import {CxGrid} from "../cmx/CxGrid";
import {
    CxTextField,
    CxCancelSaveButton,
    CxCheckBox,
    CxCloseButton,
    CxComboxSelection,
    CxMetaInfo,
} from "../cmx/CxForm";
import {CxFormFeedback} from "../cmx/CxFeedback";
import {CxSubtitle} from "../cmx/CxTypography";
import {Crew} from "../domain/Crew"
import {initialCall, runningCall} from "../gateway/CallService";
import {crewSave} from "../gateway/CrewService";
import {DateUtil} from "../lib/DateUtil"
import {ValidationUtil} from "../lib/ValidationUtil";

// --------------------------------------------------------------------------------------------------------------------
// RenderCrew
// --------------------------------------------------------------------------------------------------------------------

/**
 * RenderCrew with types: ["crew", "contractor"]
 */
const RenderCrew = (type, readOnly, currentData=null) => {
    const isUpdate = currentData !== null;

    const {user, handleAction, currentReturnTo} = useAppCtx();
    const {crew} = useCrewCtx();

    const [formValues, setFormValues] = useState(isUpdate ? Crew.mapDbToForm(currentData, type) : Crew.getFormValues(type));
    const [formErrors, setFormErrors] = useState(Crew.getFormErrors(type));
    const [call, setCall] = useState(initialCall())

    //
    // handlers
    //
    const handleChange = (fieldOrEvent, value) => {
        if (typeof fieldOrEvent === "string") {
            // For DatePicker
            setFormValues((prev) => ({ ...prev, [fieldOrEvent]: DateUtil.convertDateToIsoString(value) }));
            setFormErrors((prev) => ({ ...prev, [fieldOrEvent]: false }));
        } else {
            // For TextField or CheckBox
            const { name, value, type, checked} = fieldOrEvent.target;
            if (type === "checkbox") {
                // For CheckBox: Convert checked state to 0/1
                setFormValues((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));

                // reset dependent fields and field errors
                if (name === "active" && !checked) {
                    setFormValues((prev) => ({
                        ...prev,
                        login_enabled: 0,
                        login_reset_pw: 0,
                        login_pw: ""
                    }));
                    setFormErrors((prev) => ({
                        ...prev,
                        login_name: false,
                        login_pw: false
                    }));
                }
                if (name === "login_enabled" && !checked) {
                    setFormValues((prev) => ({
                        ...prev,
                        login_reset_pw: 0,
                        login_pw : ""
                    }));
                    setFormErrors((prev) => ({
                        ...prev,
                        login_name: false,
                        login_pw: false
                    }));
                }
                if (name === "login_reset_pw" && !checked) {
                    setFormValues((prev) => ({
                        ...prev,
                        login_pw : ""
                    }));
                    setFormErrors((prev) => ({
                        ...prev,
                        login_pw: false
                    }));
                }
                if (name === "login_enabled" && checked && formValues['login_name'] === "") {
                    const login_name = Crew.generateLoginName(formValues['firstname'], formValues['lastname'])
                    setFormValues((prev) => ({
                        ...prev,
                        login_name: login_name
                    }));
                }

            } else {
                // For TextField: Sanitize and update value
                setFormValues((prev) => ({ ...prev, [name]: value }));
            }
            setFormErrors((prev) => ({ ...prev, [name]: false }));
        }
    };

    const handleComboxChange = (field, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [field]: value
        }));

        // reset error
        setFormErrors((prev) => ({ ...prev, [field]: false }));
    };

    const handleSubmit = async (action) => {
        if (action === "save") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy, type);

            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save (create or update)
            setCall(runningCall);
            const responseCall = await crewSave(Crew.mapFormToDb(formValues, type));
            if (responseCall.isError) {
                setCall(responseCall)
                return
            }
        }
        // return (after cancel, close or successfull save action)
        const returnTo = currentReturnTo ? currentReturnTo : type;
        handleAction(returnTo)
    };


    //
    // validation
    //
    function validate(formValues, formErrors, type) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues);

        // validate required fields
        let requiredFields = Crew.getFormRequiredFields(type);
        if (type === "crew") {
            if (formValues.role === 'foreman') {
                requiredFields.push('initials');
            }
            if (formValues.login_enabled === 1) {
                requiredFields.push('login_name');
            }
            if (formValues.login_reset_pw === 1) {
                requiredFields.push('login_pw');
            }
        }
        isValid = ValidationUtil.required(formValues, formErrors, requiredFields);

        // validate team membership
        formErrors.active = ""
        if (type === "crew" && isUpdate && formValues.active === 0) {
            const isLead = crew.isLeadOfTeam(formValues.id);
            if (isLead) {
                isValid = false;
                formErrors.active = "Der Mitarbeiter ist noch Gruppenleiter und kann daher nicht deaktiviert werden.";
            } else {
               const isMember = crew.isMemberOfTeam(formValues.id);
               if (isMember) {
                   isValid = false;
                   formErrors.active = "Der Mitarbeiter ist noch Mitglied in der Gruppe '" + isMember + "' und kann daher nicht deaktiviert werden.";
               }
            }
        }

        // validate unique initials
        if (formValues.initials !== "") {
            if (crew.initialsExists(formValues.initials, formValues.id)) {
                isValid = false;
                formErrors.initials = "Die Initialen existieren bereits. Bitte eindeutige Bezeichnung verwenden."
            }
        }

        // validate login name
        if (formValues.login_name !== "") {
            const isNewLogin = ! isUpdate;
            const isUpdateWithLoginChange = isUpdate && formValues.login_name !== currentData.login_name;

            if (isNewLogin || isUpdateWithLoginChange) {
                if (crew.loginExists(formValues.login_name)) {
                    isValid = false;
                    formErrors.login_name = "Der Benutzername existiert bereits. Bitte eindeutige Bentuzernamen verwenden."
                }
            }
        }

        // return final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formTextField = (field, label, maxLength, readOnly=false) => {
        return <CxTextField field={field} label={label} maxLength={maxLength} readOnly={readOnly}
                            handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCheckBox = (field, label, readOnly=false) => {
        return <CxCheckBox field={field} label={label} readOnly={readOnly}
                           handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items, readOnly=false) => {
        return <CxComboxSelection id={field} name={label} value={formValues[field]}
                                  onChange={(value) => handleComboxChange(field, value)}
                                  items={items} readOnly={readOnly} formErrors={formErrors} />
    }

    const formCloseButton = () => {
        return <CxCloseButton handleSubmit={handleSubmit} />
    }

    const formSaveButton = () => {
        return <CxCancelSaveButton handleSubmit={handleSubmit} />
    }

    const renderCrew = (user) => {
        const roActive = !readOnly && formValues['active']  === 1 ? false : true;
        const roLogin = !roActive && formValues['login_enabled']  === 1 ? false : true;
        const roPw = !roActive && !roLogin && formValues['login_reset_pw'] === 1 ? false : true;
        const initialsLabel = formValues['role'] !== 'foreman' ? "Initialen" : "Initialen *";

        return (
            <>
                <CxFormFeedback call={call} />

                <CxGrid size="xl">{ formCheckBox("active", "Aktiv", readOnly) }</CxGrid>

                <CxGrid size="sm">{ formTextField("initials", initialsLabel, 16, roActive) }</CxGrid>
                <CxGrid size="sm">{ formCombox("role", "Rolle (Berechtigung) *",
                    readOnly ? Crew.getRoleSelection('operator') : Crew.getRoleSelection(user.role), roActive) }</CxGrid>
                <CxGrid size="lg">{ formTextField("skills", "Funktion", 64, roActive) }</CxGrid>

                <CxGrid size="lg">{ formTextField("firstname", "Vorname *", 64, roActive) }</CxGrid>
                <CxGrid size="lg">{ formTextField("lastname", "Nachname *", 64, roActive) }</CxGrid>

                <CxGrid size="lg">{ formTextField("address", "Adresse", 64, roActive) }</CxGrid>
                <CxGrid size="sm">{ formTextField("zip", "PLZ", 32, roActive) }</CxGrid>
                <CxGrid size="sm">{ formTextField("city", "Ort", 32, roActive) }</CxGrid>

                <CxGrid size="lg">{ formTextField("email", "E-Mail", 255, roActive) }</CxGrid>
                <CxGrid size="sm">{ formTextField("phone", "Telefon", 32, roActive) }</CxGrid>
                <CxGrid size="sm">{ formTextField("private_phone", "Telefon Privat", 32, roActive) }</CxGrid>

                <CxGrid size="xl"><CxSubtitle>Login</CxSubtitle></CxGrid>

                <CxGrid size="lg">{ formCheckBox("login_enabled", "Login aktivieren", roActive) }</CxGrid>
                <CxGrid size="lg">{ formCheckBox("login_reset_pw", "Passwort setzen", roLogin) }</CxGrid>
                <CxGrid size="lg">{ formTextField("login_name", roLogin ? "Benutzername" : "Benutzername *", 255, roLogin) }</CxGrid>
                <CxGrid size="lg">{ formTextField("login_pw", roPw ? "Passwort" : "Passwort *", 255, roPw) }</CxGrid>

                <CxGrid size="xl" justifyContent="flex-end">
                    {readOnly ? formCloseButton() : formSaveButton()}
                </CxGrid>

                <CxMetaInfo values={formValues} />
            </>
        );
    }

    const renderContractor = () => {
        const roActive = !readOnly && formValues['active']  === 1 ? false : true;

        return (
            <>
                <CxFormFeedback call={call} />

                <CxGrid size="xl">{ formCheckBox("active", "Aktiv", readOnly) }</CxGrid>

                <CxGrid size="lg">{ formTextField("company", "Firma *", 128, roActive) }</CxGrid>
                <CxGrid size="lg">{ formTextField("skills", "Funktion", 64, roActive) }</CxGrid>

                <CxGrid size="lg">{ formTextField("address", "Adresse", 64, roActive) }</CxGrid>
                <CxGrid size="sm">{ formTextField("zip", "PLZ", 32, roActive) }</CxGrid>
                <CxGrid size="sm">{ formTextField("city", "Ort", 32, roActive) }</CxGrid>

                <CxGrid size="lg">{ formTextField("email", "E-Mail", 255, roActive) }</CxGrid>
                <CxGrid size="lg">{ formTextField("phone", "Telefon", 32, roActive) }</CxGrid>

                <CxGrid size="xl" justifyContent="flex-end">
                    {readOnly ? formCloseButton() : formSaveButton()}
                </CxGrid>

                <CxMetaInfo values={formValues} />
            </>
        );
    }

    //
    // return form content
    //
    return (
        <>
            { type === "crew"       && renderCrew(user) }
            { type === "contractor" && renderContractor() }
        </>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// Create, Update and Delete
// --------------------------------------------------------------------------------------------------------------------

export function CrewCreate({type}) {
    const title = Crew.getLabel(type) + " erstellen";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderCrew(type, false) }
            </CxGrid>
        </Box>
    );
}

export function CrewUpdate({type}) {
    const { currentData } = useAppCtx();
    let title = Crew.getLabel(type) + " bearbeiten";

    // check is user's role is allowed to update the current data user based on the role
    const readOnly = !accessCtrl.canEditCrewRoles.includes(currentData['role']);
    if (readOnly) {
        const label = Crew.getRoleLabel(currentData['role']);
        title = title + " (für " + label + " nicht erlaubt)";
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderCrew(type, readOnly, currentData) }
            </CxGrid>
        </Box>
    );
}

export function CrewShow({type}) {
    const { currentData } = useAppCtx();
    const title = Crew.getLabel(type) + " anzeigen";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderCrew(type, true, currentData) }
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.